/* eslint-disable multiline-ternary */

import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import PubSub from '@paytrace/pt-skel/Event/PubSub'
import { AppCtx } from '@paytrace/pt-skel/Context'
import RecurringBaseLayout from '../../Component/Recurring/RecurringBaseLayout'
import RecurForm from '../../Component/Recurring/RecurForm'
import RecurSelect from '../../Component/Recurring/RecurSelect'
import Modal from '@paytrace/pt-skel/Component/Modal'
import RecurringChangeLog from '../../Component/RecurringChangeLog'
import { useRecurFormLogic } from '../../hooks/useRecurFormLogic'
import Event from '../../Event'
import { Customer, Recur } from '../../types'
import StateCheckAlert from '../../Component/Recurring/StateCheckAlert'
import { Fieldset, Legend } from '@paytrace/pt-skel/Component'
import RecurringCustomerSelect, { CustomerSelectProps } from '../../Component/Recurring/RecurringCustomerSelect'

const self = Symbol('RecurEdit')

export default function RecurEdit (): React.ReactElement {
  const context = useContext(AppCtx)
  const merchantId = Number(context.merchant)

  const [selectedRecur, setSelectedRecur] = useState<Recur | null>(null)
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [isChangeLogModalOpen, setIsChangeLogModalOpen] = useState<boolean>(false)
  const [searchParams] = useSearchParams()
  const recurIdOverWrite = searchParams.get('recurid')

  const {
    formData,
    handleChange,
    handleSubmit,
    handleDelete,
    showValidationErrors,
    supportedRecurTranxTypes,
    supportedStdEntryClasses,
    achMerchantSettings,
    minDate,
    recurResponse,
    isLoading,
    disableSubmit,
    showSurchargeInfo,
    isStateAvailable,
    isStateSurchargeAvailable,
    isStateCheckAlertOpen,
    setIsStateCheckAlertOpen,
    showDeleteButton,
    customerAchDetails,
  } = useRecurFormLogic({
    isEditMode: true,
    initialRecurData: selectedRecur,
    customer,
  })

  const [searchObj] = useState<CustomerSelectProps['searchObject']>({
    limit: 250,
    offset: 0,
    merchantId: context.merchant,
    name_filter: '',
  })

  const handleRecurChange = useCallback((recur: Recur | null) => {
    if (recur) {
      setSelectedRecur(recur)
      if (recur.customer_id) {
        PubSub.pub(Event.GET_CUSTOMER_REQUEST, { customerId: recur.customer_id })
      } else {
        setCustomer(null)
      }
    } else {
      setSelectedRecur(null)
      setCustomer(null)
    }
  }, [])

  useEffect(() => {
    const handleCustomerResponse = (data: { customer: Customer } | null) => {
      if (data?.customer) {
        setCustomer(data.customer)
      } else {
        setCustomer(null)
      }
    }

    PubSub.sub(self, Event.GET_CUSTOMER_RESPONSE, handleCustomerResponse)

    return () => {
      PubSub.unsub(Event.GET_CUSTOMER_RESPONSE)
    }
  }, [])

  const handleChangeLogClick = () => {
    if (selectedRecur?.recurring_id) {
      const request = {
        merchant_id: Number(merchantId),
        recurring_id: selectedRecur.recurring_id,
        limit: 10,
        offset: 0,
      }
      PubSub.pub(Event.GET_RECURRING_CHANGE_LOG_REQUEST, request)
      setIsChangeLogModalOpen(true)
    } else {
      console.error('Cannot open change log: No recurring payment selected.')
    }
  }

  useEffect(() => {
    if (recurResponse.message && recurResponse.status === 'success') {
      setCustomer(null)
      // handles select same recur after successful edit/delete
      PubSub.pub(Event.GET_RECURRENCE_LIST_REQUEST, { merchant_id: merchantId })
    }
  }, [recurResponse.message, recurResponse.status])

  const showForm = selectedRecur && customer && (!recurResponse.message || recurResponse.status !== 'success')

  return (
    <RecurringBaseLayout
      title="Edit Recurring Payment"
      helpLink="https://paytrace.atlassian.net/wiki/spaces/HD/pages/2027487634/New+Payment"
    >
      <RecurSelect
        merchantId={Number(merchantId)}
        handleRecurChange={handleRecurChange}
        recurIdOverWrite={Number(recurIdOverWrite)}
      />

      {recurResponse.message && (
        <div
          className={recurResponse.status === 'success' ? 'success' : 'error'}
          style={{ marginTop: '1rem' }}
        >
          {recurResponse.message}
        </div>
      )}

      {showValidationErrors()}

      {showForm && customer && (
        <>
        <Fieldset>
            <Legend title="Select Customer" />
            <RecurringCustomerSelect
              searchObject={searchObj}
              initialCustomerId={customer.customer_id}
              onCustomerSelect={setCustomer}
            />
          </Fieldset>
          <RecurForm
            formData={formData}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            handleDelete={handleDelete}
            handleChangeLogClick={handleChangeLogClick}
            supportedRecurTranxTypes={supportedRecurTranxTypes}
            supportedStdEntryClasses={supportedStdEntryClasses}
            achMerchantSettings={achMerchantSettings}
            minDate={minDate}
            isEditPage={true}
            isLoading={isLoading}
            submitDisabled={disableSubmit}
            showSurchargeInfo={showSurchargeInfo}
            showDeleteButton={showDeleteButton}
            customer={customer}
            customerAchDetails={customerAchDetails}
          />
        </>
      )}

      <StateCheckAlert
        isOpen={isStateCheckAlertOpen}
        setIsOpen={() => setIsStateCheckAlertOpen(false)}
        isSurchargeAvailable={isStateSurchargeAvailable}
        isStateAvailable={isStateAvailable}
      />
      <Modal
        open={isChangeLogModalOpen}
        onClose={() => setIsChangeLogModalOpen(false)}
        disableBackdropClick={false}
        disableEscapeKeyDown={false}
        className="modal-change-log"
        width="85%"
      >
        <RecurringChangeLog
          currentRecord={formData}
          showSurchargeInfo={showSurchargeInfo}
          setIsChangeLogButtonClicked={setIsChangeLogModalOpen}
        />
      </Modal>
    </RecurringBaseLayout>
  )
}
